/**
 * ONE READING OF "HOW WERE THE TRAILS" — the twin of
 * `App\Support\Conditions\TrailsStatus`, which carries the reasoning.
 * `harness/trailsstatus_verify` runs both over the same counts.
 *
 *   every trail closed        → closed
 *   more than half on caution → caution
 *   any trail closed          → partial ("some closed")
 *   otherwise                 → open
 *
 * Caution is open trail: it counts toward "X of Y open". Maintenance
 * is not.
 */

export const CLOSED_CONDITIONS = ['closed', 'maintenance'];

/** @returns {'open'|'caution'|'partial'|'closed'|null} */
export function trailsStatus(total, closed, caution) {
    if (!(total > 0)) return null;
    if (closed >= total) return 'closed';
    if (caution * 2 > total) return 'caution';
    return closed > 0 ? 'partial' : 'open';
}

/** From effective trail conditions (after the cascade). */
export function trailsStatusOf(conditions) {
    let total = 0;
    let closed = 0;
    let caution = 0;
    for (const c of conditions) {
        total++;
        if (CLOSED_CONDITIONS.includes(c)) closed++;
        else if (c === 'caution') caution++;
    }
    return { status: trailsStatus(total, closed, caution), total, open: total - closed, caution, closed };
}

/** A trail that can be used: open, caution, or no condition at all. */
export const isTrailOpen = (condition) => !CLOSED_CONDITIONS.includes(condition);

/** The three-tone surfaces' colour for a status. */
export function trailsStatusTone(status) {
    if (status === 'closed') return 'closed';
    if (status === 'caution' || status === 'partial') return 'caution';
    return 'open';
}

/**
 * The sentence for a count, the one every headline says:
 * "All trails are open", "Most trails are on caution", "8 of 12 trails
 * open", "All trails are closed".
 */
export function trailsStatusHeadline(r) {
    if (!r || !r.total) return 'No trails yet';
    if (r.status === 'closed') return 'All trails are closed';
    if (r.status === 'caution') return 'Most trails are on caution';
    if (r.status === 'partial') return `${r.open} of ${r.total} trails open`;
    return 'All trails are open';
}

/** The line under a headline: "7 of 10 open · 2 on caution · 3 closed". */
export function trailsStatusDetail(r) {
    if (!r || !r.total) return '';
    const parts = [`${r.open} of ${r.total} open`];
    if (r.caution) parts.push(`${r.caution} on caution`);
    if (r.closed) parts.push(`${r.closed} closed`);
    return parts.join(' · ');
}
